/**
 * =============================================================================
 * AUTOMATION BUILDER - WORKFLOW EDITOR LAYOUT
 * =============================================================================
 * 
 * PURPOSE:
 * This component is the LAYOUT CONTAINER for the workflow builder interface.
 * It arranges the ToolsBar (left), WorkflowStack (center), and panels (right).
 * 
 * RESPONSIBILITIES:
 * 1. Layout management for the builder view (flex container)
 * 2. Passes props to child components (pure pass-through, no state)
 * 3. Handles mobile vs desktop layout differences
 * 
 * COMPONENT HIERARCHY:
 * AutomationBuilder
 *   ├── BuilderHeader (top navigation bar)
 *   ├── ToolsBar (left sidebar with draggable action items)
 *   ├── WorkflowStack (center canvas with workflow steps)
 *   ├── LeadsMonitorPanel (right panel - toggleable)
 *   └── ConfigPanel (right panel - opens when node selected)
 * 
 * PROPS RECEIVED:
 * - workflowSteps: Array of workflow step objects (from mockData)
 * - selectedNode: ID of currently selected step (for highlighting)
 * - showLeadsPanel/showConfigPanel: Panel visibility flags
 * - configType: Type of selected node (for ConfigPanel)
 * - monitorLeads: Array of leads data (for LeadsMonitorPanel)
 * - Handlers: onBack, onNodeClick, onToggleLeadsPanel, etc.
 * 
 * IMPORTANT FOR DRAG-AND-DROP:
 * - This component is a pure layout component (stateless)
 * - It does NOT own workflowSteps - just passes them through
 * - DnD context/providers should wrap THIS component or its children
 * - The WorkflowStack will be the DROP TARGET
 * - The ToolsBar items will be DRAG SOURCES
 * 
 * LAYOUT STRUCTURE (Desktop):
 * ┌────────────────────────────────────────────────────────────┐
 * │                    BuilderHeader                             │
 * ├─────────┬─────────────────────────────────────┬────────────┤
 * │ Tools   │         WorkflowStack               │  Config/   │
 * │ Bar     │         (Canvas)                    │  Leads     │
 * │ (64px)  │         (flex-1)                    │  Panel     │
 * │         │                                     │  (380px)   │
 * │ [Email] │    ┌─────────────────────┐         │            │
 * │ [SMS]   │    │  Trigger         │         │            │
 * │ [Wait]  │    ├─────────────────────┤         │            │
 * │         │    │  SMS             │         │            │
 * │         │    ├─────────────────────┤         │            │
 * │         │    │  Wait            │         │            │
 * │         │    └─────────────────────┘         │            │
 * │         │    ┌- - - - - - - - - ┐         │            │
 * │         │    │  Drop Zone       │         │            │
 * │         │    └- - - - - - - - - ┘         │            │
 * └─────────┴─────────────────────────────────────┴────────────┘
 * =============================================================================
 */
import React from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import BuilderHeader from './components/BuilderHeader';
import ToolsBar from './components/ToolsBar';
import WorkflowStack from './components/WorkflowStack';
import LeadsMonitorPanel from './panels/LeadsMonitorPanel';
import ConfigPanel from './panels/ConfigPanel';
import { DRAG_TYPES } from './constants/dragTypes';

const getStartValidation = (triggerConfig, hasWorkflowActions) => {
  if (!hasWorkflowActions) {
    return { canStart: false, reason: 'Add steps first' };
  }

  const entryType = triggerConfig?.entryType || 'form';

  if (entryType === 'form') {
    if (!triggerConfig?.formId) {
      return { canStart: false, reason: 'Select a form first' };
    }
    return { canStart: true, reason: '' };
  }

  if (entryType === 'manual') {
    const selectedLeadCount = triggerConfig?.selectedLeadIds?.length || 0;
    if (selectedLeadCount === 0) {
      return { canStart: false, reason: 'Select at least one lead' };
    }
    return { canStart: true, reason: '' };
  }

  return { canStart: false, reason: 'Unknown trigger type' };
};

/**
 * AutomationBuilder Component
 * 
 * @param {Object} props
 * @param {Array} props.workflowSteps - Array of step objects defining the workflow sequence
 * @param {string|null} props.selectedNode - ID of the currently selected workflow step
 * @param {Object|null} props.selectedStep - Currently selected step object
 * @param {boolean} props.showLeadsPanel - Whether the leads monitor panel is visible
 * @param {boolean} props.showConfigPanel - Whether the config panel is visible
 * @param {string|null} props.configType - Type of node being configured ('trigger', 'email', 'sms', 'wait')
 * @param {Array} props.automationRuns - Real-time execution runs from Firestore
 * @param {boolean} props.runsLoading - Whether runs are currently loading
 * @param {Object} props.runStats - Aggregated statistics { total, running, paused, completed, failed }
 * @param {Function} props.onResumeRun - Handler to resume a paused run: (runId) => void
 * @param {Function} props.onStopRun - Handler to stop a running run: (runId) => void
 * @param {Function} props.onBack - Handler to navigate back to library view
 * @param {Function} props.onNodeClick - Handler called when a workflow node is clicked
 * @param {Function} props.onToggleLeadsPanel - Handler to toggle leads panel visibility
 * @param {Function} props.onShowGuardrails - Handler to show guardrails modal
 * @param {Function} props.onCloseLeadsPanel - Handler to close leads panel
 * @param {Function} props.onCloseConfig - Handler to close config panel
 * @param {Function} props.onSaveNodeConfig - Persist node config on Done: (stepId, config) => Promise<void>
 * @param {Function} props.onAddStep - Handler to add a new step: (type, index) => void
 * @param {Function} props.onRemoveStep - Handler to remove a step: (stepId) => void
 * @param {Function} props.onReorderSteps - Handler to reorder steps: (fromIndex, toIndex) => void
 * @param {boolean} props.isMobile - Whether the device is mobile
 */
const AutomationBuilder = ({
  currentAutomation,
  workflowSteps,
  selectedNode,
  selectedStep,
  showLeadsPanel,
  showConfigPanel,
  configType,
  // ========== LEAD MONITOR DATA (Real-time from Firestore) ==========
  automationRuns = [],     // Real-time execution runs
  runsLoading = false,     // Whether runs are loading
  runStats = {},           // Aggregated run statistics
  onPauseRun,              // (runId) => void - Pause a running/waiting run
  onResumeRun,             // (runId) => void - Resume a paused run
  onStopRun,               // (runId) => void - Stop a running run
  onDeleteRun,             // (runId) => void - Delete a run
  // ========== LEAD MONITOR FILTERS (Firestore query-level) ==========
  monitorStatusFilter = 'all',   // Current status filter value
  monitorSourceFilter = 'all',   // Current source filter value
  onStatusFilterChange,          // (value) => void - Change status filter
  onSourceFilterChange,          // (value) => void - Change source filter
  onBack,
  onNodeClick,
  onToggleLeadsPanel,
  onShowGuardrails,
  onCloseLeadsPanel,
  onCloseConfig,
  onSaveNodeConfig,
  // =============================================================================
  // STEP MANIPULATION HANDLERS - For drag-and-drop implementation
  // =============================================================================
  // These handlers are passed from AutomationsContainer and will be wired to
  // drag-and-drop events in the next implementation phase.
  // 
  // WHY PASS THROUGH (not use here):
  // - AutomationBuilder is a LAYOUT component, not a state owner
  // - These need to reach WorkflowStack (drop target) and ToolsBar (drag source)
  // - Keeping this component stateless makes it easier to test and maintain
  // =============================================================================
  onAddStep,       // (type: string, index: number) => void
  onRemoveStep,    // (stepId: string) => void
  onReorderSteps,  // (fromIndex: number, toIndex: number) => void
  // SAVE HANDLERS
  onSave,          // () => Promise<void> - Save current automation
  onNameChange,    // (newName: string) => void - Update automation name
  isSaving,        // boolean - Whether save is in progress
  // TRIGGER CONFIG (for Add Manually)
  leads = [],               // All leads from Firestore
  leadsLoading = false,     // Whether leads are still loading
  triggerConfig = {},       // Current trigger configuration
  onTriggerConfigChange,    // Handler for trigger config changes
  forms = [],               // Available forms for form-based trigger
  // START/STOP AUTOMATION
  onStartAutomation,        // () => Promise<void> - Start/activate the automation
  onStopAutomation,         // () => Promise<void> - Stop the automation
  onTestRun,                // () => void - Open test run modal
  isMobile,
  isReadOnly = false         // boolean - Whether builder is in read-only mode (ACTIVE)
}) => {
  const triggerStep = workflowSteps.find((step) => step.type === 'trigger');
  const hasWorkflowActions = workflowSteps.length > 1;
  const { canStart: canStartAutomation, reason: startDisabledReason } = getStartValidation(triggerStep?.config, hasWorkflowActions);

  // =============================================================================
  // @dnd-kit SENSORS
  // =============================================================================
  // Sensors determine how drag interactions are detected.
  // - PointerSensor: Mouse and stylus support (8px distance before drag starts)
  // - TouchSensor: Touch screen support (200ms delay to prevent scroll conflicts)
  // =============================================================================
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  // =============================================================================
  // UNIFIED DRAG END HANDLER
  // =============================================================================
  // Handles both drag types in a single context:
  // 1. STEP: Reordering existing workflow nodes
  // 2. TOOL: Adding new steps from sidebar
  // =============================================================================
  const handleDragEnd = (event) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) return;

    const { active, over } = event;

    // No valid drop target
    if (!over) return;

    const dragType = active.data.current?.type;

    // ==========================================================================
    // CASE A: Reordering existing workflow steps
    // ==========================================================================
    if (dragType === DRAG_TYPES.STEP) {
      // Dropped on same position
      if (active.id === over.id) return;

      const fromIndex = workflowSteps.findIndex(s => s.id === active.id);
      const toIndex = workflowSteps.findIndex(s => s.id === over.id);

      // Invalid indices
      if (fromIndex === -1 || toIndex === -1) return;

      // PROTECT TRIGGER: Cannot move from or to index 0
      if (fromIndex === 0 || toIndex === 0) return;

      onReorderSteps(fromIndex, toIndex);
      return;
    }

    // ==========================================================================
    // CASE B: Adding new step from sidebar tool
    // ==========================================================================
    // NOTE: TOOL drops are now handled by WorkflowStack directly.
    // WorkflowStack uses placeholderIndex as the SINGLE SOURCE OF TRUTH,
    // ensuring the visual preview always matches the final insertion position.
    // This handler is intentionally empty for TOOL drag type.
    // ==========================================================================
    if (dragType === DRAG_TYPES.TOOL) {
      // Handled by WorkflowStack.onDragEnd using placeholderIndex
      return;
    }
  };

  // ========== MOBILE LAYOUT ==========
  // On mobile, components stack vertically:
  // Header → ToolsBar (horizontal scrollable) → WorkflowStack → Panels (overlay)
  if (isMobile) {
    return (
      <div className="flex flex-col h-full relative">
        {/* Header with back button, workflow name, and save button */}
        <BuilderHeader
          currentAutomation={currentAutomation}
          onBack={onBack}
          onSave={onSave}
          onNameChange={onNameChange}
          isSaving={isSaving}
          onToggleLeadsPanel={onToggleLeadsPanel}
          onShowGuardrails={onShowGuardrails}
          onTestRun={onTestRun}
          onStartAutomation={onStartAutomation}
          onStopAutomation={onStopAutomation}
          canStartAutomation={canStartAutomation}
          startDisabledReason={startDisabledReason}
          runStats={runStats}
          showLeadsPanel={showLeadsPanel}
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />

        {/* DndContext wraps both ToolsBar and WorkflowStack for unified drag-and-drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* ToolsBar - Drag sources for new steps */}
          <ToolsBar isMobile={isMobile} isReadOnly={isReadOnly} />

          {/* WorkflowStack - Drop target and sortable nodes */}
          <WorkflowStack
            workflowSteps={workflowSteps}
            selectedNode={selectedNode}
            onNodeClick={onNodeClick}
            onAddStep={onAddStep}
            forms={forms}
            automationStatus={currentAutomation?.status || 'DRAFT'}
            onStartAutomation={onStartAutomation}
            onStopAutomation={onStopAutomation}
            onTestRun={onTestRun}
            onRemoveStep={onRemoveStep}
            isMobile={isMobile}
            isReadOnly={isReadOnly}
          />
        </DndContext>

        {/* LeadsMonitorPanel - Shows leads currently in workflow (real-time) */}
        <LeadsMonitorPanel
          showLeadsPanel={showLeadsPanel}
          runs={automationRuns}
          runStats={runStats}
          isLoading={runsLoading}
          leads={leads}
          leadsLoading={leadsLoading}
          onPause={onPauseRun}
          onResume={onResumeRun}
          onStop={onStopRun}
          onDelete={onDeleteRun}
          onClose={onCloseLeadsPanel}
          statusFilter={monitorStatusFilter}
          sourceFilter={monitorSourceFilter}
          onStatusFilterChange={onStatusFilterChange}
          onSourceFilterChange={onSourceFilterChange}
          isMobile={isMobile}
        />

        {/* ConfigPanel - Configuration form for selected step */}
        <ConfigPanel
          showConfigPanel={showConfigPanel}
          configType={configType}
          onClose={onCloseConfig}
          selectedStep={selectedStep}
          onSaveNodeConfig={onSaveNodeConfig}
          leads={leads}
          triggerConfig={triggerConfig}
          onTriggerConfigChange={onTriggerConfigChange}
          forms={forms}
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />
      </div>
    );
  }

  // ========== DESKTOP LAYOUT ==========
  // Side-by-side layout: ToolsBar (left) | WorkflowStack (center) | Panels (right)
  return (
    <div className="flex flex-col h-full relative bg-stone-50">
      {/* Header with workflow name, monitor button, guardrails, test run, save */}
      <BuilderHeader
        currentAutomation={currentAutomation}
        onBack={onBack}
        onSave={onSave}
        onNameChange={onNameChange}
        isSaving={isSaving}
        onToggleLeadsPanel={onToggleLeadsPanel}
        onShowGuardrails={onShowGuardrails}
        onTestRun={onTestRun}
        onStartAutomation={onStartAutomation}
        onStopAutomation={onStopAutomation}
        canStartAutomation={canStartAutomation}
        startDisabledReason={startDisabledReason}
        runStats={runStats}
        showLeadsPanel={showLeadsPanel}
        isMobile={isMobile}
        isReadOnly={isReadOnly}
      />

      {/* Main content area with flex layout */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* DndContext wraps both ToolsBar and WorkflowStack for unified drag-and-drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* ToolsBar - LEFT SIDEBAR (drag sources for new steps) */}
          <ToolsBar isMobile={isMobile} isReadOnly={isReadOnly} />

          {/* WorkflowStack - MAIN CANVAS (drop target + sortable nodes) */}
          <WorkflowStack
            workflowSteps={workflowSteps}
            selectedNode={selectedNode}
            onNodeClick={onNodeClick}
            onAddStep={onAddStep}
            forms={forms}
            automationStatus={currentAutomation?.status || 'DRAFT'}
            onStartAutomation={onStartAutomation}
            onStopAutomation={onStopAutomation}
            onTestRun={onTestRun}
            onRemoveStep={onRemoveStep}
            isMobile={isMobile}
            isReadOnly={isReadOnly}
          />
        </DndContext>

        {/* LeadsMonitorPanel - Right side panel (real-time from Firestore) */}
        <LeadsMonitorPanel
          showLeadsPanel={showLeadsPanel}
          runs={automationRuns}
          runStats={runStats}
          isLoading={runsLoading}
          leads={leads}
          leadsLoading={leadsLoading}
          onPause={onPauseRun}
          onResume={onResumeRun}
          onStop={onStopRun}
          onDelete={onDeleteRun}
          onClose={onCloseLeadsPanel}
          statusFilter={monitorStatusFilter}
          sourceFilter={monitorSourceFilter}
          onStatusFilterChange={onStatusFilterChange}
          onSourceFilterChange={onSourceFilterChange}
          isMobile={isMobile}
        />

        {/* ConfigPanel - Right side panel (opens when step is clicked) */}
        <ConfigPanel
          showConfigPanel={showConfigPanel}
          configType={configType}
          onClose={onCloseConfig}
          selectedStep={selectedStep}
          onSaveNodeConfig={onSaveNodeConfig}
          leads={leads}
          triggerConfig={triggerConfig}
          onTriggerConfigChange={onTriggerConfigChange}
          forms={forms}
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />
      </div>
    </div>
  );
};

export default AutomationBuilder;
